import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  SafeAreaView,
  Switch,
  Alert,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '../screens/ThemeContext';
import { auth, storage, db } from '../private/firebaseConfig';
import { signOut, updateProfile } from 'firebase/auth';
import { useAuth } from '../private/AuthContext';
import * as ImagePicker from 'expo-image-picker';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';

const StatusBar = Platform.OS === 'web' ? () => null : require('react-native').StatusBar;

export default function ProfileScreen({ navigation }) {
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const { theme, isDarkMode, toggleTheme } = useTheme();
  const { user, userType } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [profileImage, setProfileImage] = useState(user?.photoURL);

  useEffect(() => {
    setProfileImage(user?.photoURL);
  }, [user?.photoURL]);

  const getCollectionName = () => {
    switch (userType) {
      case 'admin':
        return 'admin';
      case 'user':
        return 'users';
      case 'center':
        return 'centers';
      default:
        throw new Error('Type d\'utilisateur non reconnu');
    }
  };

  // Si l'utilisateur se déconnecte, on reste sur cet écran (adapté pour les non-connectés)

  const pickImage = async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        // aspect: [1, 1],
        quality: 0.5,
      });

      if (!result.canceled) {
        await uploadProfileImage(result.assets[0].uri);
      }
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Erreur', 'Impossible de sélectionner l\'image');
    }
  };
  const uploadProfileImage = async (uri) => {
    if (!userType) {
      Alert.alert('Erreur', 'Type d\'utilisateur non défini');
      return;
    }

    try {
      setUploadingImage(true);
      setUploadProgress(0);

      const response = await fetch(uri);
      const blob = await response.blob();

      const storage = getStorage();
      const filename = `profileImages/${getCollectionName()}/${user.uid}/profile.jpg`;
      const storageRef = ref(storage, filename);

      const uploadTask = uploadBytesResumable(storageRef, blob);

      uploadTask.on('state_changed', 
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          Alert.alert('Erreur', 'Échec du téléchargement de l\'image');
          setUploadingImage(false);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

            const collectionName = getCollectionName();
            const userRef = doc(db, collectionName, user.uid);
            const userDoc = await getDoc(userRef);

            if (userDoc.exists()) {
              await updateDoc(userRef, {
                profileImage: downloadURL
              });
            } else {
              const userData = {
                email: user.email,
                profileImage: downloadURL,
                createdAt: new Date(),
                ...(userType === 'user' ? { fullName: user?.fullName || '' } : {}),
                ...(userType === 'center' ? { centerName: user?.centerName || '' } : {})
              };
              await setDoc(userRef, userData);
            }

            // Mettre à jour le profil Firebase Auth
            await updateProfile(auth.currentUser, {
              photoURL: downloadURL
            });

            // Mettre à jour l'état local
            setProfileImage(downloadURL);

            setUploadingImage(false);
            Alert.alert('Succès', 'Photo de profil mise à jour avec succès');

          } catch (error) {
            console.error('Error updating profile:', error);
            Alert.alert('Erreur', 'Impossible de mettre à jour la photo de profil');
            setUploadingImage(false);
          }
        }
      );

    } catch (error) {
      console.error('Error uploading image:', error);
      Alert.alert('Erreur', 'Impossible de mettre à jour la photo de profil');
      setUploadingImage(false);
    }
  };

  const renderProfileImage = () => {
    if (uploadingImage) {
      return (
        <View style={[styles.profileImage, styles.loadingContainer]}>
          <ActivityIndicator size="large" color="#165ca2" />
          {uploadProgress > 0 && (
            <Text style={styles.progressText}>{Math.round(uploadProgress)}%</Text>
          )}
        </View>
      );
    }

    if (profileImage) {
      return (
        <Image
          source={{ uri: profileImage }}
          style={styles.profileImage}
          onError={() => {
            console.log('Error loading image, falling back to default');
            setProfileImage(null);
          }}
        />
      );
    }

    return (
      <View style={[styles.profileImage, styles.defaultImageContainer]}>
        <MaterialCommunityIcons name="account" size={50} color="#165ca2" />
      </View>
    );
  };


  const handleLogout = async () => {
    // Gestion différente selon la plateforme
    if (Platform.OS === 'web') {
      const confirmed = window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?');
      if (confirmed) {
        try {
          setLoading(true);
          await signOut(auth);
          // La redirection sera gérée par l'useEffect
        } catch (error) {
          console.error('Logout error:', error);
          window.alert('Une erreur est survenue lors de la déconnexion');
        } finally {
          setLoading(false);
        }
      }
    } else {
      // Afficher une alerte de confirmation sur mobile
      Alert.alert(
        'Déconnexion',
        'Êtes-vous sûr de vouloir vous déconnecter ?',
        [
          {
            text: 'Annuler',
            style: 'cancel',
          },
          {
            text: 'Déconnexion',
            style: 'destructive',
            onPress: async () => {
              try {
                setLoading(true); // Ajouter un état de chargement
                await signOut(auth);
                // La redirection sera gérée par l'useEffect
              } catch (error) {
                console.error('Logout error:', error);
                Alert.alert('Erreur', 'Une erreur est survenue lors de la déconnexion');
              } finally {
                setLoading(false);
              }
            },
          },
        ],
        { cancelable: true }
      );
    }
  };

  

  const renderMenuItem = (icon, title, subtitle, onPress, rightElement) => (
    <TouchableOpacity 
      style={[
        styles.menuItem,
        { 
          backgroundColor: theme.surface,
          borderBottomColor: theme.border 
        },
        !rightElement && { paddingRight: 10 }
      ]} 
      onPress={onPress}
    >
      <View style={styles.menuItemLeft}>
        <View style={[styles.menuIconContainer, { backgroundColor: theme.background }]}>
          <MaterialCommunityIcons name={icon} size={24} color={theme.primary} />
        </View>
        <View style={styles.menuTextContainer}>
          <Text style={[styles.menuTitle, { color: theme.text }]}>{title}</Text>
          {subtitle && <Text style={[styles.menuSubtitle, { color: theme.textSecondary }]}>{subtitle}</Text>}
        </View>
      </View>
      <View style={styles.menuItemRight}>
        {rightElement || (
          <MaterialCommunityIcons name="chevron-right" size={24} color={theme.textSecondary} />
        )}
      </View>
    </TouchableOpacity>
  );

  const renderLogoutButton = () => (
    <TouchableOpacity 
      style={styles.logoutButton}
      onPress={handleLogout}
      disabled={loading}
    >
      {loading ? (
        <ActivityIndicator color="#fff" />
      ) : (
        <>
          <MaterialCommunityIcons name="logout" size={20} color="#fff" />
          <Text style={styles.logoutText}>Déconnexion</Text>
        </>
      )}
    </TouchableOpacity>
  );

  const renderSection = (title, children) => (
    <View style={styles.section}>
      <Text style={[styles.sectionTitle, { color: theme.text }]}>{title}</Text>
      <View style={[styles.sectionContent, { backgroundColor: theme.surface, borderColor: theme.border }]}>{children}</View>
    </View>
  );

  // ===== Écran pour utilisateur NON connecté =====
  if (!user) {
    return (
      <SafeAreaView style={[styles.safeArea, {backgroundColor: theme.primary}]}>
        <StatusBar barStyle="light-content" backgroundColor="#165ca2" />
        <ScrollView style={[styles.container, { backgroundColor: theme.background }]}>
          <LinearGradient
            colors={['#165ca2', '#1976D2']}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={styles.header}
          >
            <View style={styles.profileHeader}>
              <View style={[styles.profileImage, styles.defaultImageContainer]}>
                <MaterialCommunityIcons name="account" size={50} color="#165ca2" />
              </View>
              <TouchableOpacity
                style={styles.editProfileButton}
                onPress={() => navigation.navigate('LoginScreen')}
              >
                <MaterialCommunityIcons name="login" size={20} color="#fff" style={{ marginRight: 5 }} />
                <Text style={styles.editProfileText}>Connexion</Text>
              </TouchableOpacity>
            </View>
          </LinearGradient>

          <View style={[styles.content, { backgroundColor: theme.background }]}>
            <View style={styles.guestMessageContainer}>
              <MaterialCommunityIcons name="account-lock" size={60} color={theme.primary} />
              <Text style={[styles.guestTitle, { color: theme.text }]}>
                Vous n'êtes pas connecté
              </Text>
              <Text style={[styles.guestSubtitle, { color: theme.textSecondary }]}>
                Connectez-vous pour accéder à votre profil, vos documents téléchargés et bien plus encore.
              </Text>
            </View>

            {renderSection('Paramètres', (
              <>
                {renderMenuItem(
                  'theme-light-dark',
                  'Mode sombre',
                  'Changer l\'apparence',
                  () => {},
                  <Switch
                    value={isDarkMode}
                    onValueChange={toggleTheme}
                    trackColor={{ false: '#ccc', true: theme.primary }}
                    thumbColor="#fff"
                  />
                )}
              </>
            ))}

            {renderSection('Support', (
              <>
                {renderMenuItem(
                  'frequently-asked-questions',
                  'FAQ',
                  'Questions fréquentes',
                  () => navigation.navigate('FAQItem')
                )}
                {renderMenuItem(
                  'headphones',
                  'Support client',
                  'Obtenir de l\'aide',
                  () => navigation.navigate('SupportScreen')
                )}
                {renderMenuItem(
                  'information',
                  'À propos',
                  'Version 1.0.3',
                  () => navigation.navigate('AboutScreen')
                )}
              </>
            ))}

            <View style={styles.guestButtonsContainer}>
              <TouchableOpacity
                style={styles.guestLoginButton}
                onPress={() => navigation.navigate('LoginScreen')}
              >
                <MaterialCommunityIcons name="login" size={20} color="#fff" />
                <Text style={styles.guestLoginText}>Connexion</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.guestRegisterButton, { borderColor: theme.primary }]}
                onPress={() => navigation.navigate('RegisterScreen')}
              >
                <MaterialCommunityIcons name="account-plus" size={20} color={theme.primary} />
                <Text style={[styles.guestRegisterText, { color: theme.primary }]}>Inscription</Text>
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.bottomSpace} />
        </ScrollView>
      </SafeAreaView>
    );
  }

  // ===== Écran pour utilisateur CONNECTÉ =====
  return (
    <SafeAreaView style={[styles.safeArea, {backgroundColor: theme.primary}]}>
      <StatusBar barStyle="light-content" backgroundColor="#165ca2" />
      <ScrollView style={[styles.container, { backgroundColor: theme.background }]}>
        <LinearGradient
          colors={['#165ca2', '#1976D2']}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={styles.header}
        >
          <View style={styles.profileHeader}>
            <View style={[styles.profileImageContainer]}>
              {renderProfileImage()}
              <TouchableOpacity
                style={styles.editImageButton}
                onPress={pickImage}
                disabled={uploadingImage}
              >
                <MaterialCommunityIcons name="camera" size={17} color="#fff" />
              </TouchableOpacity>
            </View>
            <Text style={styles.profileName}>{user?.userType === 'user' ? user?.fullName : user?.centerName}</Text>
            <Text style={styles.profileEmail}>{user?.email}</Text>
            <TouchableOpacity
              style={styles.editProfileButton}
              onPress={() => navigation.navigate('EditProfileScreenUser')}
            >
              <Text style={styles.editProfileText}>Modifier le profil</Text>
            </TouchableOpacity>
          </View>
        </LinearGradient>

        <View style={[styles.content, { backgroundColor: theme.background }]}>
          {renderSection('Mes Documents', (
            <>
              {renderMenuItem(
                'file-document-multiple',
                'Documents téléchargés',
                'Mes documents',
                () => navigation.navigate('HistoriqueScreenUser2')
              )}
            </>
          ))}

          {renderSection('Paramètres', (
            <>
              {renderMenuItem(
                'theme-light-dark',
                'Mode sombre',
                'Changer l\'apparence',
                () => {},
                <Switch
                  value={isDarkMode}
                  onValueChange={toggleTheme}
                  trackColor={{ false: '#ccc', true: theme.primary }}
                  thumbColor="#fff"
                />
              )}
            </>
          ))}

          {renderSection('Support', (
            <>
              {renderMenuItem(
                'frequently-asked-questions',
                'FAQ',
                'Questions fréquentes',
                () => navigation.navigate('FAQItem')
              )}
              {renderMenuItem(
                'headphones',
                'Support client',
                'Obtenir de l\'aide',
                () => navigation.navigate('SupportScreen')
              )}
              {renderMenuItem(
                'information',
                'À propos',
                'Version 1.0.3',
                () => navigation.navigate('AboutScreen')
              )}
            </>
          ))}

          {renderLogoutButton()}
        </View>

        <View style={styles.bottomSpace} />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#165ca2',
  },
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    borderBottomLeftRadius: 30,
    borderBottomRightRadius: 30,
  },
  profileHeader: {
    alignItems: 'center',
    paddingVertical: 30,
  },
  profileImageContainer: {
    position: 'relative',
    marginBottom: 15,
  },
  profileImage: {
    width: 100,
    height: 100,
    borderRadius: 50,
    borderWidth: 2,
    borderColor: '#fff',
  },
  editImageButton: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    backgroundColor: '#165ca2',
    padding: 5,
    borderRadius: 20,
    borderWidth: 2,
    borderColor: '#fff',
  },
  profileName: {
    color: '#fff',
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  profileEmail: {
    color: '#fff',
    opacity: 0.9,
    marginBottom: 15,
  },
  editProfileButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 20,
  },
  editProfileText: {
    color: '#fff',
    fontWeight: '600',
  },
  content: {
    padding: 20,
  },
  section: {
    marginBottom: 25,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 15,
  },
  sectionContent: {
    backgroundColor: '#fff',
    borderRadius: 15,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.1)',
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f3f5',
  },
  menuItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  menuItemRight: {
    marginLeft: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  menuIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#f8f9fa',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15,
  },
  menuTextContainer: {
    flex: 1,
    marginRight: 10,
  },
  menuTitle: {
    fontSize: 16,
    color: '#333',
    marginBottom: 2,
  },
  menuSubtitle: {
    fontSize: 13,
    color: '#666',
  },
  logoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#C62828',
    padding: 15,
    borderRadius: 15,
    marginTop: 10,
  },
  logoutText: {
    color: '#fff',
    fontWeight: '600',
    marginLeft: 10,
  },
  bottomSpace: {
    height: 100,
  },
  loadingContainer: {
    backgroundColor: '#f8f9fa',
    justifyContent: 'center',
    alignItems: 'center',
  },
  defaultImageContainer: {
    backgroundColor: '#f8f9fa',
    justifyContent: 'center',
    alignItems: 'center',
  },
  progressText: {
    marginTop: 5,
    color: '#165ca2',
    fontSize: 12,
  },
  guestMessageContainer: {
    alignItems: 'center',
    paddingVertical: 30,
    paddingHorizontal: 20,
  },
  guestTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginTop: 15,
    marginBottom: 8,
    textAlign: 'center',
  },
  guestSubtitle: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
  },
  guestButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
    marginBottom: 25,
    gap: 12,
  },
  guestLoginButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 15,
    gap: 8,
    backgroundColor: '#165ca2',
    borderRadius: 15,
  },
  guestLoginText: {
    color: '#fff',
    fontWeight: '600',
    marginLeft: 5,
  },
  guestRegisterButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 15,
    gap: 8,
    borderWidth: 1,
    borderRadius: 15,
  },
  guestRegisterText: {
    fontWeight: '600',
    marginLeft: 5,
  },
});